/**
 * Anant Safety QR — Admin Panel
 * Manage QRs, subscriptions, scan logs, spam IPs, grant free QRs.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Shield, QrCode, Ban, Gift, RefreshCw, Flag, Printer, Plus, Trash2, Edit3, X, Search, Check } from 'lucide-react';
import api from '../../utils/api';
import BulkMessenger from '../../components/BulkMessenger';

export default function SafetyQRAdmin() {
  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [subs, setSubs] = useState([]);
  const [qrs, setQrs] = useState([]);
  const [spam, setSpam] = useState({ blocked_ips: [], reports: [] });
  // Grant dialog state
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantQuery, setGrantQuery] = useState('');
  const [grantResults, setGrantResults] = useState([]);
  const [grantUser, setGrantUser] = useState(null);
  const [grantQty, setGrantQty] = useState(10);
  const [grantDays, setGrantDays] = useState(30);
  const [grantBusy, setGrantBusy] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [printers, setPrinters] = useState([]);
  const load = async () => {
    try {
      const [o, s, q, sp, pr] = await Promise.all([
        api.get('/safety-qr/admin/overview'),
        api.get('/safety-qr/admin/subscriptions'),
        api.get('/safety-qr/admin/qrs'),
        api.get('/safety-qr/admin/spam'),
        api.get('/safety-qr/admin/printers'),
      ]);
      setOverview(o.data); setSubs(s.data.subscriptions); setQrs(q.data.qrs); setSpam(sp.data);
      setPrinters(pr.data.printers || []);
    } catch { toast.error('Load failed'); }
  };
  useEffect(() => { load(); }, []);

  const blockIP = async () => {
    const ip = prompt('IP address to block?');
    if (!ip) return;
    const hours = parseInt(prompt('For how many hours?', '24')) || 24;
    await api.post('/safety-qr/admin/spam/block', { ip, hours, reason: 'Admin action' });
    toast.success('Blocked'); load();
  };

  const openGrantDialog = () => {
    setGrantUser(null); setGrantQuery(''); setGrantResults([]);
    setGrantQty(10); setGrantDays(30); setGrantOpen(true);
  };

  // Debounced user search
  useEffect(() => {
    if (!grantOpen) return;
    const q = grantQuery.trim();
    if (q.length < 1) { setGrantResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get('/safety-qr/admin/users/search', { params: { q, limit: 8 } });
        setGrantResults(r.data.users || []);
      } catch { setGrantResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [grantQuery, grantOpen]);

  const submitGrant = async () => {
    if (!grantUser) { toast.error('Pehle user select karo'); return; }
    if (grantQty < 1 || grantQty > 100) { toast.error('QR count 1–100 hona chahiye'); return; }
    setGrantBusy(true);
    try {
      const r = await api.post('/safety-qr/admin/grant', { user_id: grantUser.id, days: grantDays, qr_count: grantQty });
      toast.success(`✅ Granted ${r.data.qr_count} QRs to @${grantUser.username} for ${grantDays} days`);
      setGrantOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setGrantBusy(false); }
  };

  const recentGrants = useMemo(() => (subs || [])
    .filter((s) => s.plan_id === 'admin_grant')
    .slice(0, 10), [subs]);

  const healGhosts = async () => {
    if (!window.confirm('Purane ghost grants ko real users pe migrate karein? (safe operation)')) return;
    try {
      const r = await api.post('/safety-qr/admin/heal-ghosts');
      toast.success(`✅ Healed ${r.data.healed} ghost grant(s). Unmatched: ${r.data.unmatched.length}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="min-h-screen pt-20 pb-12 px-4 bg-gradient-to-br from-slate-950 via-red-950/40 to-slate-950 text-white" data-testid="safety-admin">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-red-400 text-xs font-bold uppercase tracking-widest">Admin · Safety QR</div>
            <h1 className="text-3xl font-extrabold">Safety QR Control Panel</h1>
          </div>
          <div className="flex gap-2">
            <button onClick={openGrantDialog} data-testid="admin-grant-qr-btn" className="px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-bold flex items-center gap-1"><Gift className="w-3 h-3" /> Grant QR</button>
            <button onClick={() => setBulkOpen(true)} disabled={subs.length === 0} data-testid="safety-admin-bulk-btn" className="px-3 py-1.5 rounded-lg bg-blue-500 text-white text-xs font-bold flex items-center gap-1 disabled:opacity-40">📢 Bulk Msg</button>
            <button onClick={healGhosts} data-testid="admin-heal-ghosts-btn" className="px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-bold flex items-center gap-1" title="Fix old grants stuck on usernames"><RefreshCw className="w-3 h-3" /> Heal Ghosts</button>
            <button onClick={blockIP} className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-bold flex items-center gap-1"><Ban className="w-3 h-3" /> Block IP</button>
            <button onClick={load} className="p-2 rounded-lg bg-white/5"><RefreshCw className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="flex gap-2 mb-4 overflow-x-auto">
          {['overview','subscriptions','qrs','spam','printers'].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`sadm-tab-${t}`} className={`px-3 py-1.5 rounded-lg text-xs font-bold capitalize ${tab === t ? 'bg-red-500 text-white' : 'bg-white/5'}`}>{t}</button>
          ))}
        </div>
        {tab === 'overview' && overview && (
          <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatBox label="Total QRs" value={overview.total_qrs} icon={QrCode} />
            <StatBox label="Active QRs" value={overview.active_qrs} icon={Shield} accent="text-emerald-300" />
            <StatBox label="Total Scans" value={overview.total_scans} icon={QrCode} />
            <StatBox label="Total Alerts" value={overview.total_alerts} icon={Shield} accent="text-amber-300" />
            <StatBox label="Active Subs" value={overview.active_subscriptions} icon={Shield} />
            <StatBox label="Blocked IPs" value={overview.spam_ips_blocked} icon={Ban} accent="text-red-300" />
            <StatBox label="Spam Reports" value={overview.spam_reports} icon={Flag} accent="text-amber-300" />
          </div>
          <div className="mt-6 rounded-xl border border-white/10 overflow-hidden" data-testid="recent-grants-section">
            <div className="bg-white/5 px-3 py-2 text-xs uppercase text-emerald-300 font-bold flex items-center gap-2"><Gift className="w-3.5 h-3.5" /> Recent Admin Grants (last 10)</div>
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-[11px] uppercase text-slate-400"><tr><th className="text-left px-3 py-2">User</th><th className="px-3 py-2">QRs</th><th className="px-3 py-2">Days</th><th className="px-3 py-2">Expires</th><th className="px-3 py-2">Status</th></tr></thead>
              <tbody>
                {recentGrants.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-slate-500 text-xs">Koi admin grant nahi hua abhi tak.</td></tr>}
                {recentGrants.map((g) => (
                  <tr key={g.id} className="border-t border-white/5">
                    <td className="px-3 py-2"><b>@{g.user?.username || <span className="text-red-400">Ghost: {g.user_id}</span>}</b>{g.user?.full_name && <span className="text-[10px] text-slate-400 ml-2">{g.user.full_name}</span>}</td>
                    <td className="px-3 py-2 text-center font-bold text-emerald-300">{g.qr_count || 1}</td>
                    <td className="px-3 py-2 text-center text-xs">{g.duration_days || '—'}d</td>
                    <td className="px-3 py-2 text-center text-xs">{g.expires_at ? new Date(g.expires_at).toLocaleDateString() : '—'}</td>
                    <td className="px-3 py-2 text-center"><span className={`text-[10px] px-2 py-0.5 rounded-full ${g.status === 'active' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-500/20 text-slate-400'}`}>{g.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
        {tab === 'subscriptions' && (
          <div className="rounded-xl border border-white/10 overflow-hidden">
            <table className="w-full text-sm"><thead className="bg-white/5 text-xs uppercase text-slate-400"><tr><th className="text-left px-3 py-2">User</th><th className="text-left px-3 py-2">Plan</th><th className="text-right px-3 py-2">AT</th><th className="px-3 py-2">Quota</th><th className="px-3 py-2">Expires</th><th className="px-3 py-2">Status</th></tr></thead>
              <tbody>{subs.map((s) => (<tr key={s.id} className="border-t border-white/5"><td className="px-3 py-2"><b>{s.user?.full_name || s.user?.username || <span className="text-red-400 text-[10px]">Ghost: {s.user_id}</span>}</b></td><td className="px-3 py-2">{s.plan_name}</td><td className="px-3 py-2 text-right">{s.price_at}</td><td className="px-3 py-2 text-center font-bold text-emerald-300">{s.qr_count || 1}</td><td className="px-3 py-2 text-xs">{new Date(s.expires_at).toLocaleDateString()}</td><td className="px-3 py-2"><span className={`text-[10px] px-2 py-0.5 rounded-full ${s.status === 'active' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-500/20 text-slate-400'}`}>{s.status}</span></td></tr>))}
                {subs.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-slate-500 text-xs">Koi subscription nahi.</td></tr>}
              </tbody></table>
          </div>
        )}
        {tab === 'qrs' && (
          <div className="rounded-xl border border-white/10 overflow-hidden">
            <table className="w-full text-sm"><thead className="bg-white/5 text-xs uppercase text-slate-400"><tr><th className="text-left px-3 py-2">QR ID</th><th className="text-left px-3 py-2">Asset</th><th className="text-left px-3 py-2">Owner</th><th className="text-center px-3 py-2">Scans</th><th className="text-center px-3 py-2">Alerts</th><th className="px-3 py-2">Status</th></tr></thead>
              <tbody>{qrs.map((q) => (<tr key={q.qr_id} className="border-t border-white/5"><td className="px-3 py-2 font-mono text-[11px]">{q.qr_id}</td><td className="px-3 py-2">{q.asset_label}<div className="text-[10px] text-slate-500">{q.asset_type}</div></td><td className="px-3 py-2">{q.user?.full_name || q.user?.username}</td><td className="px-3 py-2 text-center">{q.scan_count}</td><td className="px-3 py-2 text-center">{q.alert_count}</td><td className="px-3 py-2"><span className={`text-[10px] px-2 py-0.5 rounded-full ${q.status === 'active' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>{q.status}</span></td></tr>))}
                {qrs.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-slate-500 text-xs">Koi QR nahi.</td></tr>}
              </tbody></table>
          </div>
        )}
        {tab === 'spam' && (
          <div className="space-y-3">
            <div>
              <h3 className="text-sm font-bold mb-2">Blocked IPs ({spam.blocked_ips.length})</h3>
              <div className="rounded-xl border border-white/10 overflow-hidden">
                <table className="w-full text-sm"><thead className="bg-white/5 text-xs uppercase text-slate-400"><tr><th className="text-left px-3 py-2">IP</th><th className="text-left px-3 py-2">Reason</th><th className="px-3 py-2">Block Until</th><th></th></tr></thead>
                  <tbody>{spam.blocked_ips.map((b) => (<tr key={b.ip} className="border-t border-white/5"><td className="px-3 py-2 font-mono">{b.ip}</td><td className="px-3 py-2 text-xs">{b.reason || '—'}</td><td className="px-3 py-2 text-xs">{b.block_until ? new Date(b.block_until).toLocaleString() : '—'}</td><td className="px-3 py-2"><button onClick={async () => { await api.post(`/safety-qr/admin/spam/unblock/${b.ip}`); toast.success('Unblocked'); load(); }} className="text-emerald-300 text-xs font-bold">Unblock</button></td></tr>))}
                    {spam.blocked_ips.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-slate-500 text-xs">Koi blocked IP nahi.</td></tr>}
                  </tbody></table>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-bold mb-2">Recent Spam Reports ({spam.reports.length})</h3>
              <div className="rounded-xl border border-white/10 overflow-hidden">
                <table className="w-full text-sm"><thead className="bg-white/5 text-xs uppercase text-slate-400"><tr><th className="text-left px-3 py-2">QR</th><th className="text-left px-3 py-2">IP</th><th className="text-left px-3 py-2">Reason</th><th className="px-3 py-2">Date</th></tr></thead>
                  <tbody>{spam.reports.map((r) => (<tr key={r.id} className="border-t border-white/5"><td className="px-3 py-2 font-mono text-[11px]">{r.qr_id}</td><td className="px-3 py-2 font-mono">{r.ip}</td><td className="px-3 py-2 text-xs">{r.reason || '—'}</td><td className="px-3 py-2 text-xs">{new Date(r.created_at).toLocaleDateString()}</td></tr>))}
                    {spam.reports.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-slate-500 text-xs">Koi report nahi.</td></tr>}
                  </tbody></table>
              </div>
            </div>
          </div>
        )}
        {tab === 'printers' && <PrintersTab printers={printers} onChange={load} />}
      </div>
      {/* Grant Dialog */}
      {grantOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !grantBusy && setGrantOpen(false)} data-testid="grant-dialog">
          <div className="w-full max-w-md rounded-2xl bg-slate-900 border border-emerald-500/30 p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2"><Gift className="w-5 h-5 text-emerald-400" /><h3 className="text-lg font-extrabold">Grant Safety QR</h3></div>
              <button onClick={() => setGrantOpen(false)} className="p-1 rounded hover:bg-white/10"><X className="w-4 h-4" /></button>
            </div>
            <label className="block text-[11px] uppercase text-slate-400 font-bold mb-1">User (username / email / name)</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-500" />
              <input
                data-testid="grant-user-search"
                value={grantQuery}
                onChange={(e) => { setGrantQuery(e.target.value); setGrantUser(null); }}
                placeholder="anant1981"
                className="w-full pl-8 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm focus:border-emerald-500 outline-none"
                autoFocus
              />
            </div>
            {grantUser ? (
              <div className="mt-2 flex items-center justify-between rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-3 py-2" data-testid="grant-user-selected">
                <div>
                  <div className="text-sm font-bold text-emerald-300">@{grantUser.username}</div>
                  <div className="text-[10px] text-slate-400">{grantUser.email}{grantUser.full_name ? ` · ${grantUser.full_name}` : ''}</div>
                </div>
                <Check className="w-4 h-4 text-emerald-400" />
              </div>
            ) : grantResults.length > 0 && (
              <div className="mt-2 rounded-lg border border-white/10 max-h-40 overflow-y-auto" data-testid="grant-user-results">
                {grantResults.map((u) => (
                  <button key={u.id} onClick={() => { setGrantUser(u); setGrantQuery(u.username || u.email); }} className="w-full text-left px-3 py-2 hover:bg-emerald-500/10 border-b border-white/5 last:border-0">
                    <div className="text-sm font-bold">@{u.username}</div>
                    <div className="text-[10px] text-slate-400">{u.email}{u.full_name ? ` · ${u.full_name}` : ''}</div>
                  </button>
                ))}
              </div>
            )}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1">
                <label className="text-[11px] uppercase text-slate-400 font-bold">QR Count</label>
                <span className="text-xl font-extrabold text-emerald-300" data-testid="grant-qty-value">{grantQty}</span>
              </div>
              <input
                data-testid="grant-qty-slider"
                type="range" min={1} max={100} value={grantQty}
                onChange={(e) => setGrantQty(parseInt(e.target.value))}
                className="w-full accent-emerald-500"
              />
              <div className="flex justify-between text-[10px] text-slate-500 mt-0.5"><span>1</span><span>25</span><span>50</span><span>75</span><span>100</span></div>
            </div>
            <div className="mt-4">
              <label className="block text-[11px] uppercase text-slate-400 font-bold mb-1">Valid For (days)</label>
              <div className="flex gap-2">
                {[7, 30, 90, 180, 365].map((d) => (
                  <button key={d} onClick={() => setGrantDays(d)} data-testid={`grant-days-${d}`} className={`flex-1 py-1.5 rounded-lg text-xs font-bold ${grantDays === d ? 'bg-emerald-500 text-white' : 'bg-white/5 text-slate-300 hover:bg-white/10'}`}>{d}d</button>
                ))}
                <input type="number" value={grantDays} onChange={(e) => setGrantDays(parseInt(e.target.value) || 1)} className="w-16 px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-center" />
              </div>
            </div>
            <button
              onClick={submitGrant}
              disabled={!grantUser || grantBusy}
              data-testid="grant-submit-btn"
              className="w-full mt-5 py-2.5 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed">
              {grantBusy ? 'Granting…' : `Grant ${grantQty} QR${grantQty > 1 ? 's' : ''} for ${grantDays} days`}
            </button>
          </div>
        </div>
      )}
      <BulkMessenger open={bulkOpen} onClose={() => setBulkOpen(false)} recipients={subs.map((s) => ({ id: s.id, name: s.user?.full_name || s.user?.username, mobile: s.user?.mobile || s.user?.phone }))} defaultTemplate={'Namaste {name},\n\nAnant Safety QR update — kripya app visit karein.\n\n🚨 Anant Safety'} label="Safety QR Users ko Bulk Message" />
    </div>
  );
}

// ============ Suggested Printers Management ============
const EMPTY_PRINTER = { name: '', city: '', whatsapp: '', mobile: '', note: '', active: true, sort: 0 };
const PrintersTab = ({ printers, onChange }) => {
  const [form, setForm] = useState(EMPTY_PRINTER);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim() || !form.whatsapp.trim()) return toast.error('Name aur WhatsApp number zaroori hai');
    setBusy(true);
    try {
      const payload = { ...form, sort: Number(form.sort) || 0 };
      if (editingId) {
        await api.put(`/safety-qr/admin/printers/${editingId}`, payload);
        toast.success('Printer updated');
      } else {
        await api.post('/safety-qr/admin/printers', payload);
        toast.success('Printer added');
      }
      setForm(EMPTY_PRINTER); setEditingId(null); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this printer?')) return;
    await api.delete(`/safety-qr/admin/printers/${id}`);
    toast.success('Deleted'); onChange();
  };

  return (
    <div className="space-y-4" data-testid="sadm-printers-tab">
      <div className="rounded-xl border border-amber-400/30 bg-amber-500/10 p-3 text-xs text-amber-100">
        🖨️ Ye printers user ko QR download karte waqt "Suggested Printers" section mein dikhte hain (max 3, sort order se). Printers se referral commission ka arrangement aap offline kar sakte hain.
      </div>
      <div className="rounded-xl border border-white/10 bg-slate-900/60 p-4">
        <h3 className="text-sm font-bold mb-3 flex items-center gap-1.5"><Printer className="w-4 h-4 text-amber-300" /> {editingId ? 'Edit Printer' : 'Add Printer'}</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
          <input placeholder="Printer / Shop Name *" value={form.name} onChange={(e) => set('name', e.target.value)} data-testid="printer-form-name" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          <input placeholder="City" value={form.city} onChange={(e) => set('city', e.target.value)} data-testid="printer-form-city" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          <input placeholder="WhatsApp Number *" value={form.whatsapp} onChange={(e) => set('whatsapp', e.target.value)} data-testid="printer-form-wa" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          <input placeholder="Mobile (optional)" value={form.mobile} onChange={(e) => set('mobile', e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          <input placeholder="Note (e.g. Stickers · Metal plates)" value={form.note} onChange={(e) => set('note', e.target.value)} data-testid="printer-form-note" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          <input type="number" placeholder="Sort order" value={form.sort} onChange={(e) => set('sort', e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2" />
          <label className="flex items-center gap-2 text-xs text-slate-300">
            <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} data-testid="printer-form-active" /> Active (users ko dikhaye)
          </label>
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={save} disabled={busy} data-testid="printer-form-save" className="px-4 py-2 rounded-lg bg-emerald-500 text-white font-bold text-xs flex items-center gap-1 disabled:opacity-60">
            <Plus className="w-3.5 h-3.5" /> {busy ? 'Saving…' : editingId ? 'Update Printer' : 'Add Printer'}
          </button>
          {editingId && <button onClick={() => { setForm(EMPTY_PRINTER); setEditingId(null); }} className="px-4 py-2 rounded-lg bg-white/10 text-xs font-bold">Cancel</button>}
        </div>
      </div>
      <div className="rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-xs uppercase text-slate-400"><tr><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">City</th><th className="text-left px-3 py-2">WhatsApp</th><th className="text-left px-3 py-2">Note</th><th className="px-3 py-2">Sort</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Actions</th></tr></thead>
          <tbody>
            {printers.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-slate-500 text-xs">Koi printer nahi. Upar se add karein.</td></tr>}
            {printers.map((p) => (
              <tr key={p.id} className="border-t border-white/5" data-testid={`printer-row-${p.id}`}>
                <td className="px-3 py-2 font-bold">{p.name}</td>
                <td className="px-3 py-2 text-xs">{p.city || '—'}</td>
                <td className="px-3 py-2 font-mono text-xs">{p.whatsapp}</td>
                <td className="px-3 py-2 text-xs text-slate-400">{p.note || '—'}</td>
                <td className="px-3 py-2 text-center">{p.sort}</td>
                <td className="px-3 py-2 text-center"><span className={`text-[10px] px-2 py-0.5 rounded-full ${p.active ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-500/20 text-slate-400'}`}>{p.active ? 'active' : 'hidden'}</span></td>
                <td className="px-3 py-2 flex gap-1 justify-center">
                  <button onClick={() => { setForm({ name: p.name, city: p.city || '', whatsapp: p.whatsapp, mobile: p.mobile || '', note: p.note || '', active: !!p.active, sort: p.sort || 0 }); setEditingId(p.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }} data-testid={`printer-edit-${p.id}`} className="p-1 rounded hover:bg-white/10"><Edit3 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => del(p.id)} data-testid={`printer-del-${p.id}`} className="p-1 rounded hover:bg-red-500/20 text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const StatBox = ({ label, value, icon: Icon, accent = 'text-red-300' }) => (
  <div className="rounded-xl border border-white/10 bg-slate-900/50 p-3">
    <div className="flex items-center gap-2 mb-1"><Icon className={`w-4 h-4 ${accent}`} /><span className="text-[11px] uppercase text-slate-400">{label}</span></div>
    <div className="text-2xl font-extrabold">{value}</div>
  </div>
);
